import { GAData } from "../../../models";
import { isRunningOnClientSide } from "../../../shared/util";

// Check we are on the client and Google's dataLayer object exists
// We also need google_tag_manager loaded since we use it sometimes to
// trigger navigation in onClick (to ensure GA events have been sent)
const googleAnalyticsCodeCanRun = (): boolean =>
    isRunningOnClientSide() && window.dataLayer && window.google_tag_manager;

/**
 *
 * @param data
 * @return {Promise.<>} promise resolved after the event has been sent (or a while), typically used to
 * give the data a chance before navigating to a different page. Only rejected when not running on the
 * client side.
 */
export const send = (data: GAData, shouldClearEcommerce = false): Promise<unknown> => {
    if (data.eventCallback) {
        throw new Error("Don't use data.eventCallback, do the action on the .then of the returned Promise");
    }
    if (!isRunningOnClientSide()) return Promise.reject();

    // Timeout for invoking the callback (typically navigation). When GA or GTM is missing, set it to timeout at once
    // since the callback will not complete normally.
    const callbackTimeoutMs = googleAnalyticsCodeCanRun() ? 2000 : 0;
    return Promise.race([
        /* Resolve when sending the event via GA and GTM completed */
        new Promise((resolve) => {
            window.dataLayer = window.dataLayer || [];
            if (shouldClearEcommerce) {
                window.dataLayer.push({ ecommerce: null });
            }
            window.dataLayer.push({
                ...data,
                eventCallback: resolve,
                // eventTimeout: 2000 // Doesn't seem to work, doing it via Promise.race
            });
        }),
        /* Resolve after 2s even if for some reason the callback wouldn't be called [yet]; we don't want to break navigation when GA/GTM has network/other issues */
        new Promise((resolve) => setTimeout(resolve, callbackTimeoutMs)),
    ]);
};
